import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./entity-select.dialog.scoped.scss";
import { Modal } from "react-bootstrap";
import { Box, Button, CircularProgress, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import {
  ENTITY_SELECT_CONFIG,
  EntitySelectModalProps,
} from "../../types/entity-select";
import useEntityQuery from "../../../../store/hooks/useEntityQuery";
import { icons } from "../../../../statics/icons";

function normalizeRows(raw: unknown): any[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const o = raw as Record<string, unknown>;
    if (Array.isArray(o.items)) return o.items as any[];
    if (Array.isArray(o.data)) return o.data as any[];
  }
  return [];
}

function rowKey(item: any, index: number): string {
  if (item?.id != null) return String(item.id);
  if (item?.slug != null) return String(item.slug);
  return `idx-${index}`;
}

function rowMatchesCurrent(item: any, current: number | string): boolean {
  if (item == null || current === "") return false;
  if (item.id != null && String(item.id) === String(current)) return true;
  if (item.slug != null && String(item.slug) === String(current)) return true;
  return false;
}

const EntitySelectDialog: React.FunctionComponent<EntitySelectModalProps> = ({
  show,
  onClose,
  entity,
  currentEntityId,
  onSelect,
}) => {
  const { t } = useTranslation();
  const config = ENTITY_SELECT_CONFIG[entity];
  const { data, loading } = useEntityQuery(entity, config.fetch, show);
  const rows = useMemo(() => normalizeRows(data), [data]);

  const [saving, setSaving] = useState(false);
  const [selectedSingleKey, setSelectedSingleKey] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!show) {
      setSelectedSingleKey(null);
      setSaving(false);
      return;
    }

    setSaving(false);

    if (currentEntityId != null && currentEntityId !== "") {
      const idx = rows.findIndex((item) =>
        rowMatchesCurrent(item, currentEntityId),
      );
      setSelectedSingleKey(idx >= 0 ? rowKey(rows[idx], idx) : null);
    } else {
      setSelectedSingleKey(null);
    }
  }, [show, entity, rows, currentEntityId]);

  const getLabel = useCallback(
    (item: any): string => {
      const raw = item?.[config.labelKey];
      if (raw == null) return "";
      if (typeof raw === "string") return t(raw);
      return String(raw);
    },
    [config.labelKey, t],
  );

  /** Raw settings key from API (e.g. HIGH_RISK) for risk levels. */
  const getItemIconKey = useCallback(
    (item: any): string | undefined => {
      if (!config.iconKey) return undefined;
      const raw =
        item?.[config.iconKey] ?? item?.iconKey ?? item?.icon_key ?? "";
      if (raw == null || raw === "") return undefined;
      return String(raw).trim();
    },
    [config.iconKey],
  );

  /**
   * Resolves to a public URL for `<img src>` (e.g. risk level icons under
   * `icons.PAGES.SETTINGS.ICON_OPTIONS`).
   */
  const getItemIconUrl = useCallback(
    (iconKey?: string, isActive = false): string => {
      if (!iconKey) {
        return isActive ? icons.categoryWhite : icons.categoryPrimary;
      }
      const map = icons.PAGES.SETTINGS.ICON_OPTIONS as Record<string, string>;
      const primary = map[iconKey];
      if (isActive) {
        const whiteKey = `${iconKey}_WHITE`;
        return map[whiteKey] ?? primary ?? icons.categoryWhite;
      }
      return primary ?? icons.categoryPrimary;
    },
    [],
  );

  const getRowIconSrc = useCallback(
    (item: any, isSelected: boolean): string | "" => {
      if (!config.iconKey) return "";
      const key = getItemIconKey(item);
      if (!key) return "";
      return getItemIconUrl(key, isSelected);
    },
    [config.iconKey, getItemIconKey, getItemIconUrl],
  );

  const handleRowClick = useCallback((item: any, index: number) => {
    const key = rowKey(item, index);
    setSelectedSingleKey((prev) => (prev === key ? null : key));
  }, []);

  const isRowSelected = useCallback(
    (item: any, index: number) => {
      const key = rowKey(item, index);
      return selectedSingleKey === key;
    },
    [selectedSingleKey],
  );

  const hasSelection = selectedSingleKey != null;

  const handleSave = useCallback(async () => {
    if (!hasSelection || saving) return;

    const found = rows.find((item, i) => rowKey(item, i) === selectedSingleKey);
    if (!found) return;

    setSaving(true);
    try {
      await Promise.resolve(onSelect?.(found));
      onClose(true);
    } catch (e) {
      console.error("EntitySelectDialog onSelect failed:", e);
    } finally {
      setSaving(false);
    }
  }, [hasSelection, saving, rows, selectedSingleKey, onSelect, onClose]);

  const handleCancel = useCallback(() => {
    if (!saving) onClose(false);
  }, [onClose, saving]);

  return (
    <Modal
      show={show}
      onHide={handleCancel}
      centered
      dialogClassName="modal-user modal-lg"
    >
      <Modal.Header
        closeButton
        style={{ borderBottom: "1px solid var(--border)" }}
      >
        <Modal.Title>{t(config.header)}</Modal.Title>
      </Modal.Header>
      <Box className="entity__head">
        <img src={icons.PAGES.CASE_DETAILS.ENTITY_HEAD} alt="" />
      </Box>
      <Modal.Body>
        <Box className="entity__body">
          <Box className="entity__info">
            <Typography variant="h2" color="textPrimary">
              {t(config.title)}
            </Typography>
            <Typography variant="body1" color="textPrimary">
              {t(config.brief)}
            </Typography>
          </Box>
          <Box className="entities__container">
            <Box className="entity__items">
              {loading ? (
                <>
                  <Skeleton variant="rect" height={48} className="mb-2" />
                  <Skeleton variant="rect" height={48} className="mb-2" />
                  <Skeleton variant="rect" height={48} className="mb-2" />
                </>
              ) : rows.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {t("USECASE_DETAILS.NO_OPTIONS_AVAILABLE")}
                </Typography>
              ) : (
                rows.map((item, index) => {
                  const selected = isRowSelected(item, index);
                  const key = rowKey(item, index);
                  const iconSrc = getRowIconSrc(item, selected);
                  return (
                    <Box
                      className={`entity__item${
                        selected ? " entity__item--selected" : ""
                      }`}
                      key={key}
                      onClick={() => handleRowClick(item, index)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRowClick(item, index);
                        }
                      }}
                    >
                      {iconSrc ? (
                        <img
                          className="entity__item-icon"
                          src={iconSrc}
                          alt=""
                          width={28}
                          height={28}
                        />
                      ) : null}
                      <Typography variant="subtitle2" style={{ flex: 1 }}>
                        {getLabel(item)}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outlined"
          color="primary"
          className="btn-secondary-theme"
          onClick={handleCancel}
          disabled={saving}
        >
          {t("cancel")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="btn-primary-theme"
          onClick={() => void handleSave()}
          disabled={!hasSelection || saving}
          startIcon={
            saving ? <CircularProgress size={18} color="inherit" /> : undefined
          }
        >
          {t("SAVE")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EntitySelectDialog;
